import React, { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { Phone, User, Lock, Eye, EyeOff, Sun, Moon, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import LoginOtpPanels from "@/components/auth/LoginOtpPanels";
import { createPanelLoader } from "@/panel/panelLoader";
import { API } from "@/utils/constants";

/** Klasik giriş sayfası yedeği — /login4 */
const LoginPage4 = ({ isDark, onToggleTheme, login, finalizeOtpLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpFlow, setOtpFlow] = useState(null);
  const loginLoader = useMemo(() => createPanelLoader({ isDark }), [isDark]);

  useEffect(() => {
    loginLoader.setTheme(isDark);
  }, [isDark, loginLoader]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      toast.error("E-posta/kullanıcı adı ve şifre gerekli");
      return;
    }
    setLoading(true);
    let nextOtpFlow = null;
    loginLoader.show({ message: "Lütfen bekleyiniz", fullscreen: true });
    try {
      const result = await login(username, password);
      if (result.completed) {
        toast.success("Giriş başarılı");
        setOtpFlow(null);
        return;
      }
      if (result.resumed) {
        toast.info(result.message || "Doğrulama kodunuz hâlâ geçerli, kaldığınız yerden devam edin.");
      }
      nextOtpFlow = {
        creds: result.creds,
        chooseChannel: result.choose_channel === true,
        channels: result.channels,
        phoneMasked: result.phone_masked,
        otpState: result.otp_required ? result : null,
      };
    } catch (error) {
      const data = error.response?.data;
      if (data?.blocked) {
        nextOtpFlow = {
          creds: { username, password },
          chooseChannel: false,
          blocked: true,
          retryIn: data.retry_in || 900,
        };
      } else {
        toast.error(data?.detail || "Giriş başarısız");
      }
    } finally {
      setLoading(false);
      if (nextOtpFlow) {
        setOtpFlow(nextOtpFlow);
      } else {
        loginLoader.hide();
      }
    }
  };

  const handleOtpComplete = (data) => {
    finalizeOtpLogin(data);
    setOtpFlow(null);
    toast.success("Giriş başarılı");
  };

  return (
    <div className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#020617]" : "bg-gray-50"}`}>
      <div className="absolute top-4 right-4">
        <Button variant="ghost" size="icon" onClick={onToggleTheme} className={isDark ? "text-slate-400" : "text-gray-600"}>
          {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
        </Button>
      </div>

      <Card className={`w-full max-w-md mx-4 ${isDark ? "bg-slate-900 border-slate-800" : "bg-white border-gray-200"}`}>
        <CardHeader className="text-center pb-2">
          <div className={`w-16 h-16 mx-auto mb-4 rounded-xl flex items-center justify-center ${isDark ? "bg-blue-600 glow-blue" : "bg-blue-500"}`}>
            <Phone className="w-8 h-8 text-white" />
          </div>
          <CardTitle className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>E-CDR</CardTitle>
          <p className={`text-sm ${isDark ? "text-slate-400" : "text-gray-500"}`}>Çağrı Kayıt Yönetim Paneli</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : "text-gray-700"}>E-posta veya Kullanıcı Adı</Label>
              <div className="relative">
                {username.includes("@") ? (
                  <Mail className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                ) : (
                  <User className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                )}
                <Input
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`pl-10 ${isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300"}`}
                  placeholder="E-posta veya kullanıcı adınız"
                  autoComplete="username"
                  data-testid="login-username-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label className={isDark ? "text-slate-300" : "text-gray-700"}>Şifre</Label>
              <div className="relative">
                <Lock className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDark ? "text-slate-500" : "text-gray-400"}`} />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 pr-10 ${isDark ? "bg-slate-950 border-slate-800 text-white" : "bg-white border-gray-300"}`}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? "text-slate-500" : "text-gray-400"}`}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white" data-testid="login-submit-btn">
              {loading ? "Giriş yapılıyor…" : "Giriş Yap"}
            </Button>
          </form>
        </CardContent>
      </Card>
      {otpFlow && (
        <LoginOtpPanels
          apiBase={API}
          creds={otpFlow.creds}
          isDark={isDark}
          panelLoader={loginLoader}
          initialChooseChannel={otpFlow.chooseChannel}
          initialChannels={otpFlow.channels}
          initialPhoneMasked={otpFlow.phoneMasked}
          initialOtpState={otpFlow.otpState}
          onComplete={handleOtpComplete}
          onDismiss={() => setOtpFlow(null)}
          initialBlocked={!!otpFlow.blocked}
          initialRetryIn={otpFlow.retryIn || 0}
        />
      )}
    </div>
  );
};

export default LoginPage4;
